/**
 * DetailPanel — contextual slide-in panel for structured resource viewing.
 *
 * Auto-opens when the AI returns panel-worthy tool results (Jira issues,
 * GitHub PRs, DB results, etc.). When open, the History Sidebar hides to
 * give the chat and panel proper screen real estate.
 *
 * Features:
 * - Resource history strip — clickable pills to switch between previously
 *   viewed resources in the conversation
 * - Same-resource accumulation — comments, transitions, etc. merge into
 *   the existing view instead of replacing it
 * - Dispatches to specialized renderers based on resourceType
 */

import { useEffect, useRef } from 'react'
import { useState } from 'react'
import { X, ChevronDown } from 'lucide-react'
import type { DetailPanelContent } from '../types'

// Renderers
import { MessageContent } from './MessageContent'
import { JiraIssueRenderer, JiraIssuesRenderer } from './detail-renderers/JiraRenderer'
import { GitHubIssueRenderer, GitHubPRRenderer, GitHubItemsRenderer } from './detail-renderers/GitHubRenderer'
import { ConfluencePageRenderer, ConfluencePagesRenderer } from './detail-renderers/ConfluenceRenderer'
import { DatabaseResultRenderer } from './detail-renderers/DatabaseRenderer'
import { TestCaseRenderer } from './detail-renderers/TestCaseRenderer'
import { SnapshotRenderer } from './detail-renderers/SnapshotRenderer'
import { ExecutionRenderer } from './detail-renderers/ExecutionRenderer'
import { GenericRenderer } from './detail-renderers/GenericRenderer'

// ---------------------------------------------------------------------------
// Provider icon/color
// ---------------------------------------------------------------------------

const PROVIDER_THEME: Record<string, { bg: string; text: string; border: string; label: string; pill: string }> = {
  jira: { bg: 'bg-info-soft', text: 'text-info-foreground', border: 'border-info/30', label: 'Jira', pill: 'bg-info-soft text-info-foreground border-info/30' },
  github: { bg: 'bg-foreground/[0.04]', text: 'text-foreground', border: 'border', label: 'GitHub', pill: 'bg-foreground/[0.06] text-foreground border' },
  confluence: { bg: 'bg-info-soft', text: 'text-info-foreground', border: 'border-info/30', label: 'Confluence', pill: 'bg-info-soft text-info-foreground border-info/30' },
  postgres: { bg: 'bg-accent-soft', text: 'text-accent-soft-foreground', border: 'border-accent/30', label: 'PostgreSQL', pill: 'bg-accent-soft text-accent-soft-foreground border-accent/30' },
  postgresql: { bg: 'bg-accent-soft', text: 'text-accent-soft-foreground', border: 'border-accent/30', label: 'PostgreSQL', pill: 'bg-accent-soft text-accent-soft-foreground border-accent/30' },
  testrail: { bg: 'bg-info-soft', text: 'text-info-foreground', border: 'border-info/30', label: 'TestRail', pill: 'bg-info-soft text-info-foreground border-info/30' },
  testops: { bg: 'bg-accent-soft', text: 'text-accent-soft-foreground', border: 'border-accent/30', label: 'Test Case', pill: 'bg-accent-soft text-accent-soft-foreground border-accent/30' },
}

function getProviderTheme(provider?: string) {
  if (!provider) return { bg: 'bg-foreground/[0.04]', text: 'text-foreground', border: 'border-subtle', label: 'Tool Result', pill: 'bg-foreground/[0.06] text-muted-foreground border-subtle' }
  return PROVIDER_THEME[provider.toLowerCase()] || { bg: 'bg-foreground/[0.04]', text: 'text-foreground', border: 'border-subtle', label: provider.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), pill: 'bg-foreground/[0.06] text-muted-foreground border-subtle' }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DetailPanelProps {
  content: DetailPanelContent
  visible: boolean
  onClose: () => void
  /** All resources in this conversation for the history strip */
  history: DetailPanelContent[]
  /** Switch to a different resource */
  onSwitchResource: (resourceKey: string) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DetailPanel({ content, visible, onClose, history, onSwitchResource }: DetailPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [showAllTabs, setShowAllTabs] = useState(false)

  // Escape key closes
  useEffect(() => {
    if (!visible) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [visible, onClose])

  // Scroll to top when resource changes (not on supplement merges)
  const prevKeyRef = useRef(content.resourceKey)
  useEffect(() => {
    if (visible && panelRef.current && content.resourceKey !== prevKeyRef.current) {
      panelRef.current.scrollTop = 0
      prevKeyRef.current = content.resourceKey
    }
  }, [content.resourceKey, visible])

  const theme = getProviderTheme(content.provider)

  return (
    <aside
      className={`shrink-0 border-l border-subtle bg-card flex flex-col transition-all duration-200 ease-in-out ${
        visible ? 'w-[40%] min-w-[380px] max-w-[600px] opacity-100' : 'w-0 opacity-0 overflow-hidden'
      }`}
    >
      {/* Header — neutral surface; the provider colour shows only on the
       * small label badge below, not as a full tinted band (was reading as
       * "side panel is cyan" rather than "this panel describes a testcase"). */}
      <div className="sticky top-0 z-10 border-b border-subtle bg-card">
        {/* Resource history tabs */}
        {history.length > 1 && (
          <div className="px-4 pt-2.5 pb-1 flex items-center gap-1.5 relative">
            {/* Show up to 4 recent tabs inline */}
            {history.slice(-4).map((entry) => {
              const isActive = entry.resourceKey === content.resourceKey
              const label = entry.title.includes(':') && entry.title.indexOf(':') < 15
                ? entry.title.slice(0, entry.title.indexOf(':'))
                : entry.title.length > 18 ? entry.title.slice(0, 16) + '…' : entry.title
              return (
                <button
                  key={entry.resourceKey}
                  onClick={() => { onSwitchResource(entry.resourceKey); setShowAllTabs(false) }}
                  className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition ${
                    isActive
                      ? 'bg-foreground/[0.08] text-foreground border-strong'
                      : 'bg-transparent text-muted-foreground border-subtle hover:bg-foreground/[0.04] hover:text-foreground'
                  }`}
                  title={entry.title}
                >
                  {label}
                </button>
              )
            })}
            {/* Overflow dropdown for 5+ tabs */}
            {history.length > 4 && (
              <div className="relative">
                <button
                  onClick={() => setShowAllTabs(!showAllTabs)}
                  className="shrink-0 rounded-full border border-subtle bg-card/80 px-2 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-card hover:text-foreground transition flex items-center gap-0.5"
                >
                  +{history.length - 4} <ChevronDown className="w-2.5 h-2.5" />
                </button>
                {showAllTabs && (
                  <div className="absolute top-full right-0 mt-1 bg-card border border-subtle rounded-lg shadow-lg py-1 z-20 min-w-[220px] max-h-[200px] overflow-y-auto">
                    {history.slice(0, -4).map((entry) => {
                      const pillTheme = getProviderTheme(entry.provider)
                      return (
                        <button
                          key={entry.resourceKey}
                          onClick={() => { onSwitchResource(entry.resourceKey); setShowAllTabs(false) }}
                          className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-foreground/[0.04] flex items-center gap-2 transition"
                        >
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${pillTheme.text}`} style={{ backgroundColor: 'currentColor' }} />
                          <span className="truncate text-foreground">{entry.title}</span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Title + close */}
        <div className="flex items-center justify-between gap-2 px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <span className={`text-[10px] font-semibold uppercase tracking-wide ${theme.text}`}>
              {theme.label}
            </span>
            <h3 className="text-xs font-semibold text-foreground truncate" title={content.title}>
              {content.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-subtle-foreground hover:bg-card hover:text-muted-foreground transition shrink-0"
            title="Close panel (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content with error boundary */}
      <div ref={panelRef} className="flex-1 overflow-y-auto px-4 py-4 detail-panel-content">
        <PanelErrorBoundary>
          <ResourceContent content={content} />
        </PanelErrorBoundary>
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// Content dispatch
// ---------------------------------------------------------------------------

function ResourceContent({ content }: { content: DetailPanelContent }) {
  const { resourceType, data } = content

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-muted-foreground">No data available</p>
      </div>
    )
  }

  switch (resourceType) {
    case 'jira_issue':
      return <JiraIssueRenderer data={data as Record<string, unknown>} />
    case 'jira_issues':
      return <JiraIssuesRenderer data={data as unknown[]} />
    case 'github_issue':
      return <GitHubIssueRenderer data={data as Record<string, unknown>} />
    case 'github_pr':
      return <GitHubPRRenderer data={data as Record<string, unknown>} />
    case 'github_items':
      return <GitHubItemsRenderer data={data as unknown[]} />
    case 'confluence_page':
      return <ConfluencePageRenderer data={data as Record<string, unknown>} />
    case 'confluence_pages':
      return <ConfluencePagesRenderer data={data as unknown[]} />
    case 'database_result':
      return <DatabaseResultRenderer data={data as Record<string, unknown>} />
    case 'test_case':
      return <TestCaseRenderer data={data as Record<string, unknown>} />
    case 'file_content': {
      const fileData = data as Record<string, unknown>
      const text = (fileData.text as string) || ''
      return (
        <div className="text-foreground leading-relaxed">
          <MessageContent content={text} />
        </div>
      )
    }
    case 'snapshot':
      return <SnapshotRenderer data={data as Record<string, unknown>} />
    case 'execution':
      return <ExecutionRenderer data={data as Record<string, unknown>} />
    case 'generic':
    default:
      return <GenericRenderer data={data} />
  }
}

// ---------------------------------------------------------------------------
// Error boundary for panel content — prevents renderer crashes from
// breaking the entire page
// ---------------------------------------------------------------------------

import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'

class PanelErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: string }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: '' }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Panel renderer error:', error, info.componentStack)
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
      tags: { feature: 'testops-hub', component: 'detail-panel' },
    })
  }

  componentDidUpdate(prevProps: { children: ReactNode }) {
    if (prevProps.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false, error: '' })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground mb-2">Failed to render panel content</p>
          <p className="text-xs text-subtle-foreground">{this.state.error}</p>
        </div>
      )
    }
    return this.props.children
  }
}
